import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { user, membership, loading } = useAuth();
  const [mode, setMode] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  if (!loading && user && membership) return <Navigate to="/" replace />;

  function switchMode(nextMode) {
    setMode(nextMode);
    setError('');
    setMessage('');
    if (nextMode !== 'password') setPassword('');
  }

  async function submit(event) {
    event.preventDefault();
    setSending(true);
    setError('');
    setMessage('');

    const normalizedEmail = email.trim().toLowerCase();

    if (mode === 'password') {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      setSending(false);
      if (signInError) {
        console.error(signInError);
        setError('We could not sign you in. Check your email and password, then try again.');
      }
      return;
    }

    const redirectBase = import.meta.env.VITE_APP_URL || window.location.origin;

    if (mode === 'forgot-password') {
      const { error: recoveryError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: `${redirectBase}/auth/reset-password`,
      });

      setSending(false);
      if (recoveryError) {
        console.error(recoveryError);
        setError('We could not send a password reset email. Please try again later.');
        return;
      }

      setMessage('If this email belongs to an account, a password reset link has been sent.');
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${redirectBase}/auth/callback`,
      },
    });

    setSending(false);
    if (signInError) {
      console.error(signInError);
      setError('We could not send the login link. Confirm that this email has been invited.');
      return;
    }

    setMessage('If this email has CRM access, a secure login link has been sent.');
  }

  const isForgotPassword = mode === 'forgot-password';

  return (
    <div className="center-screen login-background">
      <form className="auth-card" onSubmit={submit}>
        <div className="brand-mark">LT</div>
        <h1>{isForgotPassword ? 'Reset password' : 'Lead Tracker'}</h1>
        <p>
          {isForgotPassword
            ? 'Enter your account email and we will send a secure link to set a new password.'
            : 'Sign in with the email address that was invited to your CRM workspace.'}
        </p>

        {!isForgotPassword && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              className={mode === 'password' ? 'primary-button' : 'secondary-button'}
              type="button"
              onClick={() => switchMode('password')}
              disabled={sending}
              style={{ flex: 1 }}
            >
              Password
            </button>
            <button
              className={mode === 'magic-link' ? 'primary-button' : 'secondary-button'}
              type="button"
              onClick={() => switchMode('magic-link')}
              disabled={sending}
              style={{ flex: 1 }}
            >
              Email link
            </button>
          </div>
        )}

        <label className="field-label" htmlFor="email">Email</label>
        <input
          id="email"
          className="text-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@company.com"
        />

        {mode === 'password' && (
          <>
            <label className="field-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="text-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter your password"
            />
            <button
              type="button"
              className="secondary-button"
              onClick={() => switchMode('forgot-password')}
              disabled={sending}
              style={{ width: '100%', marginTop: '8px' }}
            >
              Forgot password?
            </button>
          </>
        )}

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <button className="primary-button" disabled={sending} type="submit">
          {mode === 'password' && (sending ? 'Signing in…' : 'Log in')}
          {mode === 'magic-link' && (sending ? 'Sending…' : 'Email me a login link')}
          {mode === 'forgot-password' && (sending ? 'Sending…' : 'Send password reset link')}
        </button>

        {isForgotPassword ? (
          <button
            className="secondary-button"
            type="button"
            onClick={() => switchMode('password')}
            disabled={sending}
            style={{ width: '100%', marginTop: '8px' }}
          >
            Back to login
          </button>
        ) : (
          <div className="small-note">
            Accounts are invite-only. Password login does not send an email.
          </div>
        )}
      </form>
    </div>
  );
}
